import { pushDialog, setLocale } from ".";
import { fetchApi } from "../../config/fetchConfig";
import I18n from "../../I18n";
import dompurify from "dompurify";
import { logoutFromSSO } from "../../utils/utils";
import { getConfigVariable } from "../../config/config";
import { localStorageEnum } from "../../components/GlobalLayout/util";

const loginSuccess = (data) => ({
    type: "LOGIN_SUCCESS",
    payload: {
        data,
    },
});

const loginError = (data) => ({
    type: "LOGIN_ERROR",
    payload: {
        data,
    },
});
export const logoutSuccess = (auth) => ({
    payload: auth,
    type: "LOGOUT_SUCCESS",
});

export const updateUserSession = (data) => ({
    type: "UPDATE_USER_SESSION",
    payload: {
        data,
    },
});

export const login =
    (username, password, authMode, fn = {}, auth = null, publicKeyUrl, locale = "en") =>
    (dispatch) => {
        const queriesBody = {
            query: `mutation($u:String!,$p:String!,$s:String,$pK:String){session(username:$u,password:$p,auth_mode:$s,jwks_url:$pK){session}}`,
            variables: {
                u: username,
                p: password,
                s: authMode,
                pK: publicKeyUrl,
            },
        };
        fetchApi("/graphql", queriesBody).then((res) =>
            res
                .json()
                .then((res) => {
                    const { data, errors } = res;

                    if (errors) {
                        if (fn.setAttempt) {
                            fn.setAttempt(true);
                        }
                        return dispatch(loginError(errors[0].message));
                    }

                    if (data.session) {
                        data.session.auth = auth;
                        dispatch(loginSuccess(data.session));
                        auth?.user?.id_token &&
                            window.localStorage.setItem(
                                localStorageEnum.SAFT_ID_TOKEN,
                                auth.user.id_token,
                            );
                        dispatch(setLocale(locale));
                    } else {
                        dispatch(
                            loginError(
                                "Invalid credentials. Make sure you are using your username and password.",
                            ),
                        );
                    }
                    if (fn.setAttempt) {
                        fn.setAttempt(true);
                    }
                })
                .catch(() =>
                    dispatch(loginError("Api error or Unknown error. Please try again later")),
                )
                .finally(() => {
                    if (fn.setAttempt) {
                        fn.setAttempt(true);
                    }
                }),
        );
    };

export const logout = (auth) => (dispatch, getState) => {
    const queriesBody = {
        query: `mutation{deleteSession{ok}}`,
    };
    fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => {
            const data = res.data || {};
            if (data?.deleteSession?.ok || res?.errors) {
                if (getConfigVariable("REACT_APP_AUTH_MODE") !== "LEGACY") {
                    logoutFromSSO(auth).then(() => {
                        dispatch(logoutSuccess(auth));
                    });
                } else {
                    dispatch(logoutSuccess(auth));
                }
            }
        })
        .catch(() => {
            if (getConfigVariable("REACT_APP_AUTH_MODE") !== "LEGACY") {
                logoutFromSSO(auth).then(() => {
                    dispatch(logoutSuccess(auth));
                });
            } else {
                dispatch(logoutSuccess(auth));
            }
        });
};

export const getFindingDetails =
    (projectId, fiscalYear, modulePrefix, ruleId, importId) => (dispatch, getState) => {
        const queriesBody = {
            query: `mutation($projectId:String,$fiscalYear:String,$modulePrefix:String,$ruleId:String,$importId:String){requestFindingDetailByRuleId(projectId:$projectId,fiscalYear:$fiscalYear,modulePrefix:$modulePrefix,ruleId:$ruleId,importId:$importId)}`,
            variables: {
                projectId: projectId,
                fiscalYear: fiscalYear,
                modulePrefix: modulePrefix,
                ruleId: ruleId,
                importId: importId,
            },
        };

        return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
            .then((res) => res.json().catch(() => ({})))
            .then((res) => {
                const errors = res?.errors;
                if (errors?.length) {
                    throw errors[0].message;
                }
                return true;
            });
    };

//RESET PASSWORD
const resetPasswordSuccess = (data) => ({
    type: "RESET_SUCCESS",
    payload: {
        data,
    },
});

const resetPasswordError = (msg) => ({
    type: "RESET_ERROR",
    payload: {
        msg: msg,
    },
});

const forgetPasswordMessage = (msg) => ({
    type: "FORGET_PASSWORD_MESSAGE",
    payload: {
        msg,
    },
});

export const forgetPassword = (email) => (dispatch) => {
    const queryBody = {
        query: `mutation($p:String!){forgetPassword(email:$p){success, message}}`,
        variables: {
            p: email,
        },
    };

    if (queryBody) {
        fetchApi("/graphql", queryBody)
            .then((res) => res.json())
            .then((res) => {
                const { data, errors } = res;
                if (errors) {
                    return dispatch(
                        forgetPasswordMessage({
                            success: false,
                            message: errors[0].message,
                        }),
                    );
                } else {
                    if ((data || {}).forgetPassword && (data || {}).forgetPassword.success) {
                        return dispatch(
                            forgetPasswordMessage({
                                success: true,
                                message: data.forgetPassword.message,
                            }),
                        );
                    }
                }
            })
            .catch(() =>
                dispatch(
                    forgetPasswordMessage({
                        success: false,
                        message: "Api error or Unknown error. Please try again later",
                    }),
                ),
            );
    }
};

export const resetPassword =
    (oldPassword, newPassword, confirmPassword) => (dispatch, getState) => {
        if (newPassword !== confirmPassword) {
            return dispatch(
                resetPasswordError('Your "new password" and "confirm password" does not match.'),
            );
        }

        const queriesBody = {
            query: `mutation($p:String!,$n:String!){reset_password(oldPassword:$p,newPassword:$n){success}}`,
            variables: {
                p: oldPassword,
                n: newPassword,
            },
        };

        if (queriesBody) {
            fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user }).then(
                (res) =>
                    res
                        .json()
                        .then((res) => {
                            const { data, errors } = res;

                            if (errors) {
                                return dispatch(resetPasswordError(errors[0].message));
                            }

                            if (
                                (data || {}).reset_password &&
                                (data || {}).reset_password.success
                            ) {
                                try {
                                    const hint = window.localStorage.getItem("loginHint");
                                    window.localStorage.clear();
                                    window.localStorage.setItem("loginHint", hint);
                                    dispatch(resetPasswordSuccess(data.session));
                                } catch (e) {
                                    console.error("100 - Local storage");
                                }
                                dispatch(logoutSuccess());
                            } else {
                                dispatch(
                                    resetPasswordError(
                                        "Invalid password. Make sure your password is correct.",
                                    ),
                                );
                            }
                        })
                        .catch(() =>
                            dispatch(
                                resetPasswordError(
                                    "Api error or Unknown error. Please try again later",
                                ),
                            ),
                        ),
            );
        }
    };

export const changePassword = (newPassword, confirmPassword, hash) => {
    return (dispatch) => {
        if (newPassword !== confirmPassword) {
            return dispatch(
                resetPasswordError('Your "new password" and "confirm password" does not match.'),
            );
        }

        const queriesBody = {
            query: `mutation($p:String!,$n:String!){change_password(newPassword:$p,hash:$n){success}}`,
            variables: {
                p: newPassword,
                n: hash,
            },
        };

        if (queriesBody) {
            fetchApi("/graphql", queriesBody).then((res) =>
                res
                    .json()
                    .then((res) => {
                        const { data, errors } = res;

                        if (errors) {
                            return dispatch(resetPasswordError(errors[0].message));
                        }

                        if ((data || {}).change_password && (data || {}).change_password.success) {
                            window.location.href = "/login";
                        } else {
                            dispatch(
                                resetPasswordError(
                                    "Invalid password. Make sure your password is correct.",
                                ),
                            );
                        }
                    })
                    .catch(() =>
                        dispatch(
                            resetPasswordError(
                                "Api error or Unknown error. Please try again later",
                            ),
                        ),
                    ),
            );
        }
    };
};

export const handleDownloadFilesAction =
    ({ files, projectId, organizationId }, pushDialog, i18n) =>
    async (dispatch, getState) => {
        for (const file of files) {
            const { id, fileLocation } = file;
            await fetchApi(
                "/api/download_file",
                { dataFileId: id, fileLocation, projectId, organizationId },
                {},
                { dispatch, user: getState()?.user },
            )
                .then((t) => {
                    return t.blob().then((b) => {
                        if (b && b.size > 20) {
                            const a = document.createElement("a");
                            a.href = dompurify.sanitize(URL.createObjectURL(b));
                            let fileBaseName;
                            if (fileLocation) {
                                fileBaseName = fileLocation.substring(
                                    fileLocation.lastIndexOf("/") + 1,
                                );
                            } else {
                                const dotIndex = file.name?.indexOf(".");
                                fileBaseName =
                                    dotIndex >= 0 ? file.name.substring(0, dotIndex) : file.name;
                            }
                            a.setAttribute("download", fileBaseName);
                            document.body.appendChild(a);
                            a.click();
                            document.body.removeChild(a);
                        } else {
                            pushDialog("AlertDialog", i18n`Document Dowload Error`, {
                                title: i18n`Something went wrong when generating the requested file`,
                            }).catch(() => null);
                        }
                    });
                })
                .catch((e) => {
                    throw new Error(e.message);
                });
        }
        return;
    };

export const sendSalesInformationEmail = (projectId, importIds) => (dispatch, getState) => {
    const queriesBody = {
        query: `mutation($p:String!,$i:[Int!]!){send_sales_information_email(projectId:$p,importIds:$i)}`,
        variables: {
            p: projectId,
            i: importIds,
        },
    };
    return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => {
            const data = res.data || {};
            if (data && data.send_sales_information_email) {
                return true;
            } else {
                throw new Error("Somethings went wrong in sales information submission");
            }
        })
        .catch((e) => {
            dispatch(
                pushDialog("ConfirmDialog", I18n.translate`Submit Error`, {
                    title: I18n.translate`Somethings went wrong in sales information submission`,
                }),
            ).catch(() => null);
            return false;
        });
};

export const upgradeAccount = () => (dispatch, getState) => {
    const queriesBody = {
        query: `mutation{upgrade_account_sso{upgraded, message}}`,
    };
    return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => {
            const data = res.data["upgrade_account_sso"] || {};
            return data;
        })
        .catch((e) => {
            dispatch(
                pushDialog("AlertDialog", I18n.translate`Submit Error`, {
                    title: I18n.translate`Somethings went wrong during account upgrade`,
                }),
            );
        });
};

export const validateAccountUpgrade = () => (dispatch, getState) => {
    const queriesBody = {
        query: `mutation{validate_account_upgrade{confirmed}}`,
    };
    return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => {
            const data = res.data["validate_account_upgrade"] || {};
            return data;
        })
        .catch(console.error);
};

export const checkValidSovosIdentity = (email) => () => {
    const queriesBody = {
        query: `query($e:String!){validate_sovos_identity(email:$e)}`,
        variables: {
            e: email,
        },
    };
    return fetchApi("/graphql", queriesBody)
        .then((res) => res.json().catch(() => ({})))
        .then((res) => {
            const data = res.data["validate_sovos_identity"] || false;
            return data;
        })
        .catch(console.error);
};

export const getOrganizationDashboardSummary =
    (organizationId, year, yearType) => (dispatch, getState) => {
        const queriesBody = {
            query: `
            query($organizationId: String, $year: String, $yearType: String) {
                organization_dashboard_summary(organizationId: $organizationId, year: $year, yearType: $yearType) {
                    summaries{ summary{key,value} },
                    years{calendaryear, fiscalyear }
                }
            }
        `,
            variables: {
                organizationId: organizationId,
                year: year,
                yearType: yearType,
            },
        };
        return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res.data["organization_dashboard_summary"] || {})
            .catch(console.error);
    };

export const getRawTablesAvailable =
    (projectId, modulePrefix, fiscalYear) => (dispatch, getState) => {
        const queriesBody = {
            query: `
            query($modulePrefix: String, $fiscalYear: String!, $projectId: String!) {
                tables_available(modulePrefix: $modulePrefix, fiscalYear: $fiscalYear, projectId: $projectId)
            }
        `,
            variables: {
                fiscalYear: fiscalYear,
                modulePrefix: modulePrefix,
                projectId: projectId,
            },
        };

        return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data || [])
            .catch(console.error);
    };

export const getRawDataModulesWithViewMode =
    (projectId, fiscalYear) => (dispatch, getState) => {
        const queriesBody = {
            query: `
            query($projectId: String!, $fiscalYear: String!) {
                raw_data_modules_with_entities(projectId: $projectId, fiscalYear: $fiscalYear) {
                    module
                    viewMode
                    entities
                }
            }
        `,
            variables: {
                projectId: projectId,
                fiscalYear: fiscalYear,
            },
        };

        return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data?.raw_data_modules_with_entities || [])
            .catch(() => []);
    };

export const getCustomCsvTablesAvailable = (projectId, modulePrefix) => (dispatch, getState) => {
    const queriesBody = {
        query: `
            query($modulePrefix: String, $projectId: String!) {
                custom_csv_tables_available(modulePrefix: $modulePrefix, projectId: $projectId)
            }
        `,
        variables: {
            modulePrefix: modulePrefix,
            projectId: projectId,
        },
    };

    return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => res?.data || [])
        .catch(console.error);
};

export const updateRowsAction =
    (projectId, modulePrefix, fiscalYear, importId, entity, updateObject) =>
    (dispatch, getState) => {
        const queriesBody = {
            query: `mutation($p:String!,$fy:String!,$mp:String!,$i:String!,$e:String!,$uObj:String){update_rows_raw_data(projectId:$p,fiscalYear:$fy,modulePrefix:$mp,importId:$i,entity:$e,updateObject:$uObj)}`,
            variables: {
                p: projectId,
                fy: fiscalYear,
                mp: modulePrefix,
                i: importId,
                e: entity,
                uObj: updateObject,
            },
        };

        return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data);
    };

export const updateRowsActionCustomCsv =
    (projectId, entityRawData, updateObject) => (dispatch, getState) => {
        console.log("entityRawData", entityRawData);
        const queriesBody = {
            query: `mutation($p:String!,$e:String!,$uObj:String){update_rows_custom_csv_raw_data(projectId:$p,entity:$e,updateObject:$uObj)}`,
            variables: {
                p: projectId,
                e: entityRawData,
                uObj: updateObject,
            },
        };

        return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
            .then((res) => res.json().catch(() => ({})))
            .then((res) => res?.data);
    };

export const updateImportProfileAction = (projectId, updateObject) => (dispatch, getState) => {
    const queriesBody = {
        query: `mutation($p:String!,$uObj:String!){update_import_profile(projectId:$p,data:$uObj)}`,
        variables: {
            p: projectId,
            uObj: updateObject,
        },
    };

    return fetchApi("/graphql", queriesBody, {}, { dispatch, user: getState()?.user })
        .then((res) => res.json().catch(() => ({})))
        .then((res) => res?.data);
};
